import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { clientModelsService, Model3DGeometry } from "../../services/client-models.service";

const EXAMPLES = [
  "Casa de 10m x 8m, 2 pisos, techo plano, 3 ventanas al frente",
  "Casa moderna de 12 por 10 metros, 1 piso, techo inclinado, concreto, 4 ventanas y 2 puertas",
  "Edificio de 15x12, 3 pisos, techo plano, acabados premium, 6 ventanas",
];

export function ModelNewPage() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insufficientTokens, setInsufficientTokens] = useState<{ required: number; balance: number } | null>(null);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInsufficientTokens(null);

    if (text.trim().length < 5) {
      setError("Describe tu proyecto (mínimo 5 caracteres)");
      return;
    }

    setIsGenerating(true);
    try {
      const result = await clientModelsService.generate(text.trim(), name.trim() || undefined);
      navigate(`/dashboard/models/${result.model.id}`, { replace: true });
    } catch (err: any) {
      if (err?.status === 402) {
        setInsufficientTokens({
          required: err.data?.required ?? 3,
          balance: err.data?.balance ?? 0,
        });
        setError(err.message);
      } else {
        setError(err?.message || "Error al generar el modelo");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <button
          onClick={() => navigate("/dashboard/models")}
          className="text-sm text-slate-500 hover:text-primary mb-2"
        >
          ← Volver a mis modelos
        </button>
        <h2 className="text-2xl font-bold text-slate-900">Visualiza tu idea con IA</h2>
        <p className="text-sm text-slate-500 mt-1">
          Describe tu proyecto y generaremos un modelo 3D interactivo. Costo: <strong>3 tokens</strong>
        </p>
      </div>

      <form onSubmit={handleGenerate} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Nombre del modelo <span className="text-slate-400 font-normal">(opcional)</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej: Casa familiar"
            disabled={isGenerating}
            maxLength={80}
            className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none transition"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Describe tu proyecto <span className="text-primary">*</span>
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            disabled={isGenerating}
            maxLength={500}
            placeholder="Ej: casa de 10m x 8m, 2 pisos, techo plano, 3 ventanas al frente"
            className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none transition resize-none"
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-slate-400">{text.length}/500</span>
          </div>
        </div>

        {/* Ejemplos */}
        <div>
          <p className="text-xs text-slate-500 mb-2">Ejemplos rápidos:</p>
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setText(ex)}
                disabled={isGenerating}
                className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-1.5 rounded-full transition text-left"
              >
                {ex.substring(0, 50)}…
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
            <p>{error}</p>
            {insufficientTokens && (
              <button
                type="button"
                onClick={() => navigate("/dashboard/tokens")}
                className="mt-2 text-primary hover:underline font-semibold"
              >
                → Recargar tokens
              </button>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={isGenerating || text.trim().length < 5}
          className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-lg font-semibold disabled:opacity-50 transition flex items-center justify-center gap-2"
        >
          {isGenerating ? (
            <>
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Generando modelo 3D...
            </>
          ) : (
            <>🪄 Generar modelo (3 tokens)</>
          )}
        </button>
      </form>
    </div>
  );
}